import { useEffect, useState } from "react";
import {
  listCuratorClaims,
  approveClaim,
  denyClaim,
  drainCandidates,
  backfillCanonicalKeys,
  mergeTrails,
  markTrailDistinct,
  listUsers,
  setUserRole,
  getMe,
  listCompCodes,
  createCompCode,
  deleteCompCode,
  ApiError,
  type BackfillDuplicate,
  type CuratorClaim,
  type UserRow,
  type Role,
  type CompCode,
} from "../api";
import { CatalogWorkspace } from "./CatalogWorkspace";
import { OrgManagementCard } from "./OrgManagementCard";
import { PlatformAnalyticsCard } from "./PlatformAnalyticsCard";
import {
  DashboardShell,
  type SidebarGroup,
} from "./DashboardShell";
import { SetStatusPill } from "./SetStatusPill";

type ClaimFilter = "pending" | "approved" | "denied";

/** Sidebar item IDs — also the keys we dispatch into the content
 *  switch below. Adding a new section means a new id here + a new
 *  case in the renderSection() switch. */
type SectionId =
  | "orgs"
  | "catalog"
  | "candidates"
  | "claims"
  | "users"
  | "comp-codes"
  | "analytics";

const SIDEBAR_GROUPS: SidebarGroup[] = [
  {
    id: "curate",
    label: "Curate",
    items: [
      { id: "orgs", label: "Organizations" },
      { id: "catalog", label: "Trail Catalog" },
      { id: "candidates", label: "Candidates" },
      { id: "claims", label: "Claims" },
    ],
  },
  {
    id: "people",
    label: "People",
    items: [{ id: "users", label: "Users" }],
  },
  {
    id: "growth",
    label: "Growth",
    items: [{ id: "comp-codes", label: "Comp codes" }],
  },
  {
    id: "insights",
    label: "Insights",
    items: [{ id: "analytics", label: "Platform analytics" }],
  },
];

const SECTION_TITLES: Record<SectionId, { title: string; subtitle?: string }> = {
  orgs: {
    title: "Organizations",
    subtitle: "Volunteer orgs that steward trails on the platform.",
  },
  catalog: {
    title: "Trail Catalog",
    subtitle: "Every trail in the public catalog — curate-edit metadata, pin, archive.",
  },
  candidates: {
    title: "Catalog candidates",
    subtitle: "Crowdsourced (url, name) pairs waiting to graduate into the catalog.",
  },
  claims: {
    title: "Claims queue",
    subtitle: "Riders asking to steward a trail. Approve to grant admin access.",
  },
  users: {
    title: "Users",
    subtitle: "Everyone who has signed in. Promote to curator, grant admin, or revoke.",
  },
  "comp-codes": {
    title: "Comp codes",
    subtitle: "Mint codes that unlock Pro for free — for trail admins, orgs, and partners.",
  },
  analytics: {
    title: "Platform analytics",
    subtitle: "How the platform's being used across every org.",
  },
};

export function CuratorPanel() {
  const [active, setActive] = useState<SectionId>("orgs");
  const { title, subtitle } = SECTION_TITLES[active];

  return (
    <DashboardShell
      groups={SIDEBAR_GROUPS}
      activeId={active}
      onSelect={(id) => setActive(id as SectionId)}
      breadcrumb={["Curator", title]}
      pageTitle={title}
      pageSubtitle={subtitle}
      contextBadge="Curator"
      quickAction={<SetStatusPill />}
    >
      {renderSection(active)}
    </DashboardShell>
  );
}

function renderSection(id: SectionId) {
  switch (id) {
    case "orgs":
      return <OrgManagementCard />;
    case "catalog":
      return <CatalogWorkspace />;
    case "candidates":
      return (
        <div className="stack">
          <DrainCandidates />
          <BackfillCanonicalKeys />
        </div>
      );
    case "claims":
      return <ClaimsSection />;
    case "users":
      return <UsersSection />;
    case "comp-codes":
      return <CompCodesSection />;
    case "analytics":
      return <PlatformAnalyticsCard />;
  }
}

// ---------------------------------------------------------------------------
// Claims queue — was inline in the old CuratorPanel; now its own
// section so the dashboard shell can render it standalone.
// ---------------------------------------------------------------------------

function ClaimsSection() {
  const [filter, setFilter] = useState<ClaimFilter>("pending");
  const [claims, setClaims] = useState<CuratorClaim[] | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setClaims(null);
    listCuratorClaims(filter)
      .then((r) => setClaims(r.items))
      .catch(() => setClaims([]));
  }, [filter, reloadKey]);

  return (
    <div className="card">
      <div className="row" style={{ justifyContent: "space-between" }}>
        <p className="muted" style={{ margin: 0 }}>
          Filed claims grouped by review state. Approve to grant admin
          access on the trail; deny with a reason so the requester
          understands.
        </p>
        <div className="row">
          {(["pending", "approved", "denied"] as ClaimFilter[]).map((f) => (
            <button
              key={f}
              className={f === filter ? "small" : "small secondary"}
              onClick={() => setFilter(f)}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {claims === null && (
        <div className="muted" style={{ marginTop: 12 }}>Loading…</div>
      )}
      {claims !== null && claims.length === 0 && (
        <div className="empty">No {filter} claims.</div>
      )}
      {claims !== null && claims.length > 0 && (
        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table>
            <thead>
              <tr>
                <th>Trail</th>
                <th className="hide-sm">Requester</th>
                <th className="hide-sm">Reason</th>
                <th className="hide-sm">Filed</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {claims.map((c) => (
                <ClaimRow
                  key={c.id}
                  claim={c}
                  onChanged={() => setReloadKey((k) => k + 1)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function ClaimRow({ claim, onChanged }: { claim: CuratorClaim; onChanged: () => void }) {
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  async function doApprove() {
    const note = prompt("Approval note (optional):") ?? undefined;
    setSubmitting(true);
    try {
      await approveClaim(claim.id, note || undefined);
      setFeedback("Approved.");
      onChanged();
    } catch (err) {
      setFeedback(err instanceof ApiError ? err.message : "Failed.");
    } finally {
      setSubmitting(false);
    }
  }

  async function doDeny() {
    const note = prompt("Denial reason:");
    if (note === null) return;
    setSubmitting(true);
    try {
      await denyClaim(claim.id, note || undefined);
      setFeedback("Denied.");
      onChanged();
    } catch (err) {
      setFeedback(err instanceof ApiError ? err.message : "Failed.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <tr>
      <td>
        <strong>{claim.trail.name}</strong>
        <div className="muted" style={{ fontSize: 12, wordBreak: "break-all" }}>
          <a href={claim.trail.statusPageUrl} target="_blank" rel="noreferrer">
            {claim.trail.statusPageUrl}
          </a>
        </div>
        <div className="muted hide-on-desktop" style={{ fontSize: 12, marginTop: 4 }}>
          {claim.requester.name ? `${claim.requester.name} · ` : ""}
          {claim.requester.email}
        </div>
      </td>
      <td className="hide-sm">
        {claim.requester.name && <div>{claim.requester.name}</div>}
        <div className={claim.requester.name ? "muted" : undefined} style={{ fontSize: 12, wordBreak: "break-all" }}>
          {claim.requester.email}
        </div>
      </td>
      <td className="muted hide-sm">{claim.reason ?? "—"}</td>
      <td className="muted hide-sm">{new Date(claim.createdAt * 1000).toLocaleDateString()}</td>
      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
        {feedback ? (
          <span className="muted" style={{ fontSize: 12 }}>{feedback}</span>
        ) : claim.status === "pending" ? (
          <>
            <button className="small" onClick={doApprove} disabled={submitting}>
              Approve
            </button>{" "}
            <button className="small danger" onClick={doDeny} disabled={submitting}>
              Deny
            </button>
          </>
        ) : (
          <span className="muted" style={{ fontSize: 12 }}>{claim.status}</span>
        )}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Catalog candidates drain — was inline; promoted to its own section
// so it's reachable from the sidebar as a first-class destination.
// ---------------------------------------------------------------------------

function DrainCandidates() {
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ promoted: number; skipped: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    if (!confirm("Promote all crowdsourced KV candidates into the catalog?")) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await drainCandidates();
      setResult({ promoted: r.promoted, skipped: r.skipped });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="card">
      <div className="row" style={{ justifyContent: "space-between" }}>
        <p className="muted" style={{ margin: 0 }}>
          Pull crowdsourced (url, name) pairs the cron has collected
          from KV and promote them into the trail catalog.
        </p>
        <button onClick={run} disabled={submitting}>
          {submitting ? "Draining…" : "Drain candidates"}
        </button>
      </div>
      {result && (
        <div className="success">
          Promoted {result.promoted}, skipped {result.skipped}.
        </div>
      )}
      {error && <div className="error">{error}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Canonical-locality-key backfill — stamps the dedup spine on existing
// rows. Surfaces collisions (groups of rows that produce the same key)
// so we can plan Round 2 (curator merge UI).
// ---------------------------------------------------------------------------

function BackfillCanonicalKeys() {
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{
    scanned: number;
    updated: number;
    duplicates: BackfillDuplicate[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    if (!confirm("Scan the catalog for trails that look like duplicates? Safe to re-run.")) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await backfillCanonicalKeys();
      setResult({
        scanned: r.scanned,
        updated: r.updated,
        // Fall back to the older `collisions` shape on old worker
        // builds so a transition deploy doesn't blank the UI.
        duplicates: r.duplicates ?? [],
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed.");
    } finally {
      setSubmitting(false);
    }
  }

  function removePair(idx: number) {
    setResult((prev) => {
      if (!prev) return prev;
      return { ...prev, duplicates: prev.duplicates.filter((_, i) => i !== idx) };
    });
  }

  return (
    <div className="card">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <p className="muted" style={{ margin: 0, marginBottom: 6 }}>
            <strong>Find duplicate trails.</strong> Scans the catalog for
            rows that look like the same place (e.g. "Allatoona Creek
            Park" vs "Allatoona Creek"). Anything it finds shows up
            below with a one-click way to merge or split them. Safe to
            run any time — it only flags pairs, never deletes on its own.
          </p>
        </div>
        <button onClick={run} disabled={submitting}>
          {submitting ? "Scanning…" : "Scan for duplicates"}
        </button>
      </div>
      {result && (
        <>
          <div className="success" style={{ marginTop: 8 }}>
            Scan complete. {result.duplicates.length === 0
              ? "No duplicates found — catalog is clean."
              : `${result.duplicates.length} possible duplicate pair${result.duplicates.length === 1 ? "" : "s"} waiting on you.`}
          </div>
          {result.duplicates.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <h4 style={{ margin: "8px 0 4px" }}>
                Possible duplicates ({result.duplicates.length})
              </h4>
              <p className="muted" style={{ margin: 0, marginBottom: 12 }}>
                For each pair below, decide whether they're really the
                same trail. If they are, pick which version to keep — the
                other's events, subscriptions and tags get folded onto
                the survivor. If they're actually different trails,
                "Keep both separate" tells the system to stop flagging
                them.
              </p>
              {result.duplicates.map((d, idx) => (
                <DuplicatePairCard
                  key={`${d.keeper.slug}-${d.pending.slug}`}
                  pair={d}
                  onResolved={() => removePair(idx)}
                />
              ))}
            </div>
          )}
        </>
      )}
      {error && <div className="error" style={{ marginTop: 8 }}>{error}</div>}
    </div>
  );
}

/// One pair of likely-duplicate trails. Shows both rows side-by-side,
/// gives the curator three actions: "Keep this one" on either row
/// (merges the other into it), or "Keep both separate" (forces the
/// pair distinct in the dedup spine).
function DuplicatePairCard({
  pair,
  onResolved,
}: {
  pair: BackfillDuplicate;
  onResolved: () => void;
}) {
  const [busy, setBusy] = useState<"merge-keeper" | "merge-pending" | "distinct" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resolvedMessage, setResolvedMessage] = useState<string | null>(null);

  async function doMerge(winnerSlug: string, loserSlug: string, winnerName: string, loserName: string, key: "merge-keeper" | "merge-pending") {
    if (!confirm(`Merge "${loserName}" into "${winnerName}"? This deletes "${loserName}" and moves its data onto "${winnerName}".`)) return;
    setBusy(key);
    setError(null);
    try {
      await mergeTrails(winnerSlug, loserSlug);
      setResolvedMessage(`Merged "${loserName}" into "${winnerName}".`);
      setTimeout(onResolved, 1200);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Merge failed.");
    } finally {
      setBusy(null);
    }
  }

  async function doMarkDistinct() {
    if (!confirm("Tell the system these two trails are actually different places? They'll both stay in the catalog and stop showing up as duplicates.")) return;
    setBusy("distinct");
    setError(null);
    try {
      // Mark the still-unstamped (pending) row as distinct — the
      // keeper already has the canonical key, so this clears the pair.
      await markTrailDistinct(pair.pending.slug);
      setResolvedMessage(`Got it — "${pair.keeper.name}" and "${pair.pending.name}" are separate trails.`);
      setTimeout(onResolved, 1200);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed.");
    } finally {
      setBusy(null);
    }
  }

  if (resolvedMessage) {
    return (
      <div className="card" style={{ marginBottom: 12, background: "var(--success-bg, rgba(0, 200, 100, 0.08))" }}>
        ✓ {resolvedMessage}
      </div>
    );
  }

  const keeperActive = busy === "merge-keeper";
  const pendingActive = busy === "merge-pending";
  const distinctActive = busy === "distinct";
  const anyBusy = busy !== null;

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <PairRow
          name={pair.keeper.name}
          subtitle={pair.keeper.state}
          buttonLabel={keeperActive ? "Merging…" : `Keep "${pair.keeper.name}"`}
          disabled={anyBusy}
          onClick={() =>
            doMerge(pair.keeper.slug, pair.pending.slug, pair.keeper.name, pair.pending.name, "merge-keeper")
          }
        />
        <PairRow
          name={pair.pending.name}
          subtitle={pair.pending.state}
          buttonLabel={pendingActive ? "Merging…" : `Keep "${pair.pending.name}"`}
          disabled={anyBusy}
          onClick={() =>
            doMerge(pair.pending.slug, pair.keeper.slug, pair.pending.name, pair.keeper.name, "merge-pending")
          }
        />
      </div>
      <div className="row" style={{ marginTop: 12, justifyContent: "flex-end" }}>
        <button className="small" onClick={doMarkDistinct} disabled={anyBusy}>
          {distinctActive ? "Saving…" : "Keep both separate"}
        </button>
      </div>
      {error && <div className="error" style={{ marginTop: 8 }}>{error}</div>}
    </div>
  );
}

function PairRow({
  name,
  subtitle,
  buttonLabel,
  disabled,
  onClick,
}: {
  name: string;
  subtitle: string | null;
  buttonLabel: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ fontWeight: 600 }}>{name}</div>
      {subtitle && <div className="muted" style={{ fontSize: 13 }}>{subtitle}</div>}
      <button onClick={onClick} disabled={disabled} style={{ alignSelf: "flex-start" }}>
        {buttonLabel}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Users — list + role management
// ---------------------------------------------------------------------------

function UsersSection() {
  const [users, setUsers] = useState<UserRow[] | null>(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selfId, setSelfId] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  useEffect(() => {
    getMe().then((u) => setSelfId(u?.id ?? null));
    load("");
  }, []);

  async function load(q: string) {
    setLoading(true);
    setError(null);
    try {
      const r = await listUsers(q);
      setUsers(r.items);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed.");
    } finally {
      setLoading(false);
    }
  }

  async function changeRole(user: UserRow, role: Role) {
    if (role === user.role) return;
    const isSelfDemotion = user.id === selfId && role !== "curator";
    if (isSelfDemotion) {
      alert("You can't demote your own curator role.");
      return;
    }
    if (!confirm(`Change ${user.email} from ${user.role} → ${role}?`)) return;
    setUpdatingId(user.id);
    setError(null);
    try {
      await setUserRole(user.id, role);
      await load(search);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed.");
    } finally {
      setUpdatingId(null);
    }
  }

  return (
    <div className="card">
      <p className="muted" style={{ marginTop: 0 }}>
        Search by name or email. Change a user's role to grant them curator
        powers or revoke admin access entirely.
      </p>
      <div className="row">
        <input
          type="text"
          placeholder="Filter by name or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load(search)}
        />
        <button onClick={() => load(search)} disabled={loading}>
          {loading ? "Loading…" : "Search"}
        </button>
      </div>

      {users && users.length === 0 && (
        <div className="empty">No users match.</div>
      )}
      {users && users.length > 0 && (
        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th className="hide-sm">Role</th>
                <th className="hide-sm">Signed up</th>
                <th className="hide-sm">Last seen</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>
                    {u.name ?? <span className="muted">—</span>}
                    {u.id === selfId && <span className="muted"> (you)</span>}
                  </td>
                  <td>{u.email}</td>
                  <td className="hide-sm">
                    <span className="pill source">{u.role}</span>
                  </td>
                  <td className="muted hide-sm">
                    {new Date(u.createdAt * 1000).toLocaleDateString()}
                  </td>
                  <td className="muted hide-sm">
                    {u.lastSeenAt
                      ? new Date(u.lastSeenAt * 1000).toLocaleDateString()
                      : "—"}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <select
                      value={u.role}
                      disabled={updatingId === u.id}
                      onChange={(e) => changeRole(u, e.target.value as Role)}
                    >
                      <option value="user">user</option>
                      <option value="admin">admin</option>
                      <option value="curator">curator</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {error && <div className="error">{error}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Comp codes — mint free-Pro grant codes (trail admins, orgs, partners).
// Riders redeem via the app; here a curator creates + tracks usage.
// ---------------------------------------------------------------------------

function CompCodesSection() {
  const [codes, setCodes] = useState<CompCode[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [maxRedemptions, setMaxRedemptions] = useState("");
  const [note, setNote] = useState("");
  const [creating, setCreating] = useState(false);
  const [justCreated, setJustCreated] = useState<string | null>(null);

  function load() {
    setCodes(null);
    listCompCodes()
      .then((r) => setCodes(r.items))
      .catch(() => setCodes([]));
  }

  useEffect(load, []);

  async function create() {
    setCreating(true);
    setError(null);
    setJustCreated(null);
    try {
      const created = await createCompCode({
        code: code.trim() || undefined,
        maxRedemptions: maxRedemptions.trim() ? Number(maxRedemptions) : null,
        note: note.trim() || undefined,
      });
      setJustCreated(created.code);
      setCode("");
      setMaxRedemptions("");
      setNote("");
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't create the code.");
    } finally {
      setCreating(false);
    }
  }

  async function revoke(c: string) {
    if (!confirm(`Revoke ${c}? Existing redemptions keep Pro; no new redemptions.`)) return;
    setError(null);
    try {
      await deleteCompCode(c);
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't revoke the code.");
    }
  }

  return (
    <div className="stack">
      <div className="card">
        <p className="muted" style={{ marginTop: 0 }}>
          Mint a code that unlocks Pro for free. Leave the code blank to
          auto-generate one. Leave the cap blank for unlimited redemptions.
        </p>
        <div className="row" style={{ gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span className="muted">Code (optional)</span>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="Auto-generate"
              maxLength={40}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span className="muted">Max redemptions</span>
            <input
              value={maxRedemptions}
              onChange={(e) => setMaxRedemptions(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="Unlimited"
              inputMode="numeric"
              style={{ width: 120 }}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 180 }}>
            <span className="muted">Note</span>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. SORBA board comp"
              maxLength={200}
            />
          </label>
          <button className="small" onClick={create} disabled={creating}>
            {creating ? "Creating…" : "Create code"}
          </button>
        </div>
        {justCreated && (
          <p style={{ marginBottom: 0 }}>
            Created <strong style={{ fontFamily: "var(--mono)" }}>{justCreated}</strong> — share
            it. Riders redeem it in the app to unlock Pro free.
          </p>
        )}
        {error && <div className="error">{error}</div>}
      </div>

      <div className="card">
        {codes === null ? (
          <p className="muted">Loading…</p>
        ) : codes.length === 0 ? (
          <p className="muted">No comp codes yet. Create one above.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Redeemed</th>
                  <th>Note</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {codes.map((c) => (
                  <tr key={c.code}>
                    <td style={{ fontFamily: "var(--mono)" }}>{c.code}</td>
                    <td>
                      {c.redeemedCount}
                      {c.maxRedemptions == null ? " / ∞" : ` / ${c.maxRedemptions}`}
                    </td>
                    <td className="muted">{c.note ?? "—"}</td>
                    <td>
                      <button className="small danger" onClick={() => revoke(c.code)}>
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
